// Reference-data hooks — products, work-sources, payment-modes, time-slots.
//
// Server status (post-Wave-B3 hotfix): all three list endpoints
//   GET /api/v1/reference-data/products
//   GET /api/v1/reference-data/work-sources
//   GET /api/v1/reference-data/payment-modes
// are live (see server `src/modules/reference-data/`). They return
// `{ items: ReferenceDataItem[] }` for the authenticated tenant.
//
// 404 / error handling (defensive — hooks must NEVER toast or retry):
//   - The fetcher swallows 404s AND any other axios error and resolves to
//     [].  React Query then sees a successful empty result, not an error,
//     so it does not toast or replay. This is intentional — the parent
//     forms degrade to the "no options — leave blank" select state and
//     the user can still complete the booking. A network blip should not
//     trigger a toast.
//   - `retry: 0` belt-and-braces — even if a non-404 error slips through
//     the catch, we never replay (no console spam).
//   - `staleTime: 1 hour` matches server cache TTL (Stage 8.5
//     `CACHE_TTL.REFERENCE_DATA = 15 m`); we extend on the client because
//     reference data is small and rarely changes during a session.
//   - `gcTime: 1 hour` so the result survives unmounts within the same
//     session and pages don't re-fire on every form mount.
//
// Time slots: live at GET /api/v1/collections/time-slots?type=weekday|weekend (mig
// 069). Falls back to the static ['AM','AM-PM','PM'] enum when the server
// returns 404 or any error so the form is usable on environments where
// the migrations haven't been applied yet.

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useAuthStore } from '../../../store/authStore';
import {
  TIME_SLOTS,
  type ReferenceDataItem,
  type ReferenceDataListResponse,
  type SlotDayType,
  type TimeSlotDto,
  type TimeSlotListResponse,
  type Timeframe,
} from '../../../types/api';

const STALE_1_HR = 60 * 60 * 1000;
const GC_1_HR = 60 * 60 * 1000;

interface UseRefListOptions {
  /** When true, the hook returns an empty array instead of firing the
   *  request (e.g. for unauthenticated callers). */
  disabled?: boolean;
}

/** Generic reference-data fetcher. 404 → [] (silent fallback for the
 *  documented gap §9.14). Other errors are also swallowed to [] because
 *  the hooks don't surface a UI for the failure: the parent forms degrade
 *  to the "no options — leave blank" select state regardless of the
 *  failure mode, and this avoids one-off network blips becoming visible
 *  console errors during page navigation. */
async function fetchRef(path: string): Promise<ReferenceDataItem[]> {
  try {
    const res = await apiClient.get<ReferenceDataListResponse>(path);
    return res.data?.items ?? [];
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return [];
    return [];
  }
}

export function useProducts(opts: UseRefListOptions = {}) {
  const companyId = useAuthStore((s) => s.user?.company_id ?? null);
  return useQuery({
    queryKey: queryKeys.referenceData.products(companyId),
    queryFn: () => fetchRef(endpoints.referenceData.products),
    staleTime: STALE_1_HR,
    gcTime: GC_1_HR,
    retry: 0,
    refetchOnWindowFocus: false,
    enabled: !opts.disabled,
  });
}

export function useWorkSources(opts: UseRefListOptions = {}) {
  const companyId = useAuthStore((s) => s.user?.company_id ?? null);
  return useQuery({
    queryKey: queryKeys.referenceData.workSources(companyId),
    queryFn: () => fetchRef(endpoints.referenceData.workSources),
    staleTime: STALE_1_HR,
    gcTime: GC_1_HR,
    retry: 0,
    refetchOnWindowFocus: false,
    enabled: !opts.disabled,
  });
}

export function usePaymentModes(opts: UseRefListOptions = {}) {
  const companyId = useAuthStore((s) => s.user?.company_id ?? null);
  return useQuery({
    queryKey: queryKeys.referenceData.paymentModes(companyId),
    queryFn: () => fetchRef(endpoints.referenceData.paymentModes),
    staleTime: STALE_1_HR,
    gcTime: GC_1_HR,
    retry: 0,
    refetchOnWindowFocus: false,
    enabled: !opts.disabled,
  });
}

/** Fetch time slots for the given day type from the server.
 *
 *  Falls back to the static ['AM','AM-PM','PM'] list if the server returns
 *  404 or any other error, so the form is usable even if migrations are not
 *  yet applied on a given environment.
 *
 *  Returns TimeSlotDto[] so callers have access to both `id` (for
 *  time_slot_id submission) and `label` (for display). */
export function useTimeSlots(type: SlotDayType, opts: UseRefListOptions = {}) {
  const companyId = useAuthStore((s) => s.user?.company_id ?? null);
  return useQuery<TimeSlotDto[]>({
    queryKey: queryKeys.referenceData.timeSlots(type, companyId),
    queryFn: async () => {
      try {
        const res = await apiClient.get<TimeSlotListResponse>(
          endpoints.referenceData.timeSlots(type),
        );
        const items = res.data?.items ?? [];
        if (items.length > 0) return items;
        // Server returned empty list — fall back to static defaults
        return TIME_SLOTS.map((label, i) => ({
          id: i + 1,
          slot_type: type,
          label,
          display_order: i,
          is_active: true,
        }));
      } catch {
        return TIME_SLOTS.map((label, i) => ({
          id: i + 1,
          slot_type: type,
          label,
          display_order: i,
          is_active: true,
        }));
      }
    },
    staleTime: STALE_1_HR,
    gcTime: GC_1_HR,
    retry: 0,
    refetchOnWindowFocus: false,
    enabled: !opts.disabled,
  });
}

/** Static fallback — returns the raw label strings from TIME_SLOTS.
 *  Use this only where you don't need the server-driven slot id. */
export function useTimeSlotsLabels(): readonly Timeframe[] {
  return TIME_SLOTS;
}
